import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Star, Send, X, CheckCircle } from 'lucide-react';

const FeedbackContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2rem;
  margin: 1rem 0;
  backdrop-filter: blur(10px);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeedbackTitle = styled.h3`
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
`;

const FeedbackDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const StarRatingContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const StarButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.filled ? '#FFD700' : 'rgba(255, 255, 255, 0.3)'};
  
  &:hover {
    transform: scale(1.1);
    color: #FFD700;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #ffffff;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    border-color: #F43F60;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeedbackButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FeedbackSubmitButton = styled.button`
  flex: 1;
  background: #F43F60;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: #E03A56;
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const FeedbackSkipButton = styled.button`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeedbackSuccess = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22C55E;
  color: #22C55E;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const ErrorMessage = styled.div`
  background: rgba(244, 63, 96, 0.1);
  border: 1px solid #F43F60;
  color: #F43F60;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const CharacterCount = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  text-align: right;
  margin-top: 0.25rem;
`;

const FeedbackForm = ({ 
  onSubmit, 
  onSkip, 
  onClose, 
  title = "How was your experience?", 
  description = "We'd love to hear about your car search experience. Your feedback helps us improve our AI assistant.",
  showCloseButton = false,
  maxCommentLength = 500
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const feedbackData = {
        rating,
        comment,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        userLocation: null // Will be set by the parent component if needed
      };

      if (onSubmit) {
        await onSubmit(feedbackData);
      }
      
      setIsSubmitted(true);
      
      // Auto close after 3 seconds
      setTimeout(() => {
        if (onClose) onClose();
      }, 3000);
      
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  if (isSubmitted) {
    return (
      <FeedbackContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <FeedbackSuccess>
          <CheckCircle size={20} />
          Thank you for your feedback! Your response helps us improve our service.
        </FeedbackSuccess>
      </FeedbackContainer>
    );
  }

  return (
    <FeedbackContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {showCloseButton && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'none'}
          >
            <X size={20} />
          </button>
        </div>
      )}

      <FeedbackTitle>{title}</FeedbackTitle>
      <FeedbackDescription>{description}</FeedbackDescription>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <StarRatingContainer>
        {[1, 2, 3, 4, 5].map((star) => (
          <StarButton
            key={star}
            type="button"
            filled={rating >= star}
            onClick={() => setRating(star)}
            disabled={isSubmitting}
            aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
          >
            <Star size={32} fill={rating >= star ? '#FFD700' : 'none'} />
          </StarButton>
        ))}
      </StarRatingContainer>

      <FormGroup>
        <FormLabel htmlFor="feedback-comment">
          Additional Comments (Optional)
        </FormLabel>
        <FeedbackTextarea
          id="feedback-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Tell us more about your experience..."
          disabled={isSubmitting}
          maxLength={maxCommentLength}
        />
        <CharacterCount>
          {comment.length}/{maxCommentLength} characters
        </CharacterCount>
      </FormGroup>

      <FeedbackButtons>
        <FeedbackSkipButton 
          type="button" 
          onClick={handleSkip}
          disabled={isSubmitting}
        >
          <X size={16} />
          Skip
        </FeedbackSkipButton>
        <FeedbackSubmitButton 
          type="button" 
          onClick={handleSubmit}
          disabled={isSubmitting || rating === 0}
        >
          {isSubmitting ? (
            <>
              <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              Submitting...
            </>
          ) : (
            <>
              <Send size={16} />
              Submit Feedback
            </>
          )}
        </FeedbackSubmitButton>
      </FeedbackButtons>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </FeedbackContainer>
  );
};

export default FeedbackForm; 